import { Button as MantineButton } from '@mantine/core';
import { PaymentPlanDetail } from 'app/Private/PaymentPlans';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { validatePairedAmountFilters } from 'shared/components/CustomTable/filters/pairedAmountFilters';
import ExportInvoices from 'shared/components/ExportInvoices';
import PostponeForm from 'shared/forms/PostponeForm';
import { Button, ButtonColor, Dropdown } from 'shared/io';
import { queryParamParser } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { PostponableInvoice } from 'types/store/invoice-state';

import Invoice from '../Invoice';
import styleIdentifiers from './imminent-reminders.module.scss';
import {
  type GetPostponableInvoicesViewParams,
  useGetPostponableInvoicesView,
} from './useGetPostponableInvoicesView';

const styles = classNames.bind(styleIdentifiers);

// Builds the view-data query params from the URL: view id from the hash, filters
// from the search (parsed the same way the legacy getView saga did).
const paramsFromLocation = (hash: string, search: string): GetPostponableInvoicesViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export default function ImminentReminders() {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const location = useLocation();

  const [params, setParams] = useState<GetPostponableInvoicesViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { postponableInvoices, isPostponableInvoicesInitialLoading, refetchPostponableInvoices } =
    useGetPostponableInvoicesView(params);

  const [selectedInvoices, setSelectedInvoices] = useState<PostponableInvoice[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);
  const [actionsDisable, setActionsDisable] = useState({
    cancel: true,
    confirm: true,
  });

  // Re-sync the query params whenever the URL changes (the orchestrator writes
  // filters/sort/pagination into the URL via history.push).
  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const handleFilters = (filters: any) =>
    validatePairedAmountFilters(['total_tvac', 'remaining_balance'])({ ...filters });

  const exportInvoices = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT),
      children: <ExportInvoices resourceType="invoice" />,
    });
  };

  const askDurationToPostpone = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.POSTPONE),
      children: (
        <PostponeForm
          invoices={areAllItemsAcrossPagesSelected ? 'all' : selectedInvoices}
          onSubmit={postponeSelectedInvoices}
          close={() => dialogHide(DialogShowId.CUSTOM)}
        />
      ),
    });
  };

  const onChangeSelection = (value: Array<unknown>) => {
    setSelectedInvoices(value as Array<PostponableInvoice>);

    if (value.length === 0) {
      setActionsDisable({
        cancel: true,
        confirm: true,
      });
    } else if (!company.automatic_reminders) {
      let isCancelDisabled = true;
      let isConfirmDisabled = true;
      for (const item of [...value] as Array<PostponableInvoice>) {
        if (item.force_reminder) {
          isCancelDisabled = false;
        } else {
          isConfirmDisabled = false;
        }
      }

      setActionsDisable({
        cancel: isCancelDisabled,
        confirm: isConfirmDisabled,
      });
    }
  };

  const postponeSelectedInvoices = ({ days_delayed }: any) => {
    invoiceActions.actionsPostponeMultiple({
      data: {
        days_delayed,
        invoices: areAllItemsAcrossPagesSelected
          ? undefined
          : selectedInvoices.map(item => ({
              id: item.id,
              payment_plan_id: item.payment_plan_id,
            })),
        filter_params: queryParamParser(qs.parse(location.search)),
        view_id: areAllItemsAcrossPagesSelected ? location.hash.slice(1) : undefined,
      },
      callback: () => {
        refetchPostponableInvoices();
        dialogHide(DialogShowId.CUSTOM);
        setActionsDisable({
          cancel: true,
          confirm: true,
        });
      },
    });
  };

  const forceReminder = (force_reminder: any) => () => {
    invoiceActions.actionsForceReminder({
      data: {
        force_reminder,
        invoices: areAllItemsAcrossPagesSelected
          ? undefined
          : selectedInvoices.map(item => ({
              id: item.id,
              payment_plan_id: item.payment_plan_id,
            })),
        filter_params: queryParamParser(qs.parse(location.search)),
        view_id: areAllItemsAcrossPagesSelected ? location.hash.slice(1) : undefined,
      },
      callback: () => {
        refetchPostponableInvoices();
        setActionsDisable({
          cancel: true,
          confirm: true,
        });
      },
    });
  };

  const cancelAllValidatedReminders = () => {
    invoiceActions.actionsForceReminder({
      data: {
        force_reminder: false,
        all_selected: true,
        view_id: location.hash.slice(1),
        filter_params: queryParamParser(qs.parse(location.search)),
      },
      callback: () => {
        refetchPostponableInvoices();
        setActionsDisable({
          cancel: true,
          confirm: true,
        });
      },
    });
  };

  const cancelReminders =
    areAllItemsAcrossPagesSelected ||
    selectedInvoices.length === postponableInvoices?.metadata?.pagination?.total_objects
      ? cancelAllValidatedReminders
      : forceReminder(false);

  const showInvoice = (item: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      const baseUrl = item.payment_plan_id ? '/payment-plans' : '/invoices';
      const id = item.payment_plan_id ? item.payment_plan_id : item.id;
      window.open(`${baseUrl}/${id}`, '_blank');
      return;
    }

    const children = item.payment_plan_id ? (
      <PaymentPlanDetail planId={item.payment_plan_id} inModal />
    ) : (
      <Invoice id={item.id} modal />
    );

    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children,
    });
  };

  return (
    <CustomTableViews
      className={styles('Actions')}
      title={t(i18nKeys.NAV.IMINENT_REMINDERS)}
      callbackAction={refetchPostponableInvoices}
      onClickRow={showInvoice}
      isLoading={isPostponableInvoicesInitialLoading}
      pagination={postponableInvoices?.metadata?.pagination}
      handleFilters={handleFilters}
      items={postponableInvoices?.data ?? []}
      onChangeSelectionCallback={onChangeSelection}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
      tableName="postponable_invoices"
      actions={
        <div className={styles('button-wrapper', !company.automatic_reminders && 'manual')}>
          {selectedInvoices.length === 0 && (
            <MantineButton onClick={exportInvoices}>{t(i18nKeys.EXPORT)}</MantineButton>
          )}
          <Button
            disabled={selectedInvoices.length === 0}
            onClick={askDurationToPostpone}
            label={t(i18nKeys.POSTPONE)}
            noMargin
          />
          {!company.automatic_reminders && (
            <>
              <Button
                disabled={actionsDisable.cancel}
                onClick={cancelReminders}
                color={ButtonColor.GREY}
                label={t(i18nKeys.INVOICING.IMPENDING_REMINDERS.CANCEL_REMINDER)}
                noMargin
              />
              <Button
                disabled={actionsDisable.confirm}
                onClick={forceReminder(true)}
                color={ButtonColor.MAIN}
                label={t(i18nKeys.INVOICE.SEND_REMINDER)}
                noMargin
              />
            </>
          )}
          {/* Same actions but in a dropdown for the mobile version */}
          <Dropdown
            className={styles('actions-dropdown')}
            selectorClass={styles(
              actionsDisable.cancel &&
                actionsDisable.confirm &&
                selectedInvoices.length === 0 &&
                'disabled',
            )}
            selectorContent={
              <Button color={ButtonColor.WHITE} label={t(i18nKeys.FORM.ACTIONS)} noMargin />
            }
          >
            <div className={styles('dropdown-item')} onClick={askDurationToPostpone}>
              {t(i18nKeys.POSTPONE)}
            </div>
            {!company.automatic_reminders && !actionsDisable.confirm && (
              <div className={styles('dropdown-item')} onClick={forceReminder(true)}>
                {t(i18nKeys.INVOICE.SEND_REMINDER)}
              </div>
            )}
            {!company.automatic_reminders && !actionsDisable.cancel && (
              <div className={styles('dropdown-item')} onClick={cancelReminders}>
                {t(i18nKeys.INVOICING.IMPENDING_REMINDERS.CANCEL_REMINDER)}
              </div>
            )}
          </Dropdown>
        </div>
      }
    />
  );
}
